import { CookedEvent, EVENT_COOKED_PAGEVIEW, PageDesc } from '@fullstory/client-api';
import { CookedPageFetcher, PageModel, SessionFetcher } from '@fullstory/playback-model';
import { int64 } from '@fullstory/proto/google/protobuf';
import { useNotifications, useXhr } from '@fullstory/react-hooks';
import { fullstorySelectors } from '@fullstory/redux';
import { S } from '@fullstory/util/sexpr';
import { prefixUrl } from '@fullstory/util/url';
import { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';

type Results = {
  cookedPageDesc: PageDesc;
  cookedPageUrl: string;
  pageOffset: number;
  pageModel: PageModel;
};

export const useObjectPreviewProps = (
  userId: string | null,
  sessionId: string | null,
  pageId: string | null,
  pageOffset: number,
  setLoading: (loading: boolean) => void,
) => {
  const notifications = useNotifications();
  const xhr = useXhr();
  const ctx = useMemo(
    () => ({
      xhr: () => xhr,
      serverApiPrefix: () => '/',
      notifications: () => notifications,
    }),
    [xhr, notifications],
  );
  const orgId = useSelector(fullstorySelectors.getOrgId);

  const [results, setResults] = useState<Results | null>(null);

  const sessionFetcher = useMemo(
    () => new SessionFetcher(ctx, 'PAGE_PREVIEW_FETCH_SESSION'),
    [ctx],
  );
  const cookedPageFetcher = useMemo(() => new CookedPageFetcher(ctx), [ctx]);

  useEffect(() => {
    const fetch = async () => {
      setResults(null);
      if (!userId || !sessionId || !pageId) return;

      // Fetch session and cooked page in parallel
      // Fetch Session Page
      const sessionExpr: S[] = [{ value: `${userId}` }, { value: `${sessionId}` }];
      const cookedPageUrl = `/cookedPage?OrgId=${orgId}&UserId=${userId}&SessionId=${sessionId}&PageId=${pageId}`;
      const fetchCookedPageUrl = prefixUrl(ctx.serverApiPrefix(), cookedPageUrl);
      const [session, cookedPage] = await Promise.all([
        sessionFetcher.fetchSessions(sessionExpr),
        cookedPageFetcher.fetchCookedPage(fetchCookedPageUrl),
      ]);

      // Fetch Cooked Page

      const pageDesc = session.Pages.find(p => pageId === p.PageId);
      if (!pageDesc) return;

      if (!cookedPage.Events) return;

      const getOffsetDelta = (event: CookedEvent): number => {
        // page views have a when timestamp of the next navigate / end of page
        // the real offset of the event is the start time
        if (event.Type === EVENT_COOKED_PAGEVIEW && event.PageViewDetails) {
          return pageOffset - int64.toNumber(event.PageViewDetails.StartTime);
        }
        // otherwise, most other events can use their when value directly
        return pageOffset - event.When;
      };
      const cookedEvent = cookedPage.Events.reduce((prev, cur) => {
        const prevOffsetDelta = getOffsetDelta(prev);
        const curOffsetDelta = getOffsetDelta(cur);
        if (curOffsetDelta >= 0 && curOffsetDelta < prevOffsetDelta) {
          return cur; // take cur instead as it is equal to or after the offset and closer
        }
        return prev;
      });
      if (!cookedEvent) return; // something went wrong

      // TODO: prefetch data and pass as `prefetched.data` in constructor below
      const pageModel = new PageModel(ctx, pageDesc, { cookedPage });

      // Prefetch raw events so that the renderer loads faster
      pageModel.prefetchData();

      setResults({
        cookedPageDesc: pageModel.getRawPageDesc(),
        cookedPageUrl: cookedEvent?.PageUrl ?? pageModel?.url ?? '',
        pageOffset,
        pageModel,
      });
    };

    const run = async () => {
      setLoading(true);
      await fetch();
      setLoading(false);
    };

    run();
  }, [
    orgId,
    userId,
    sessionId,
    pageId,
    pageOffset,
    ctx,
    cookedPageFetcher,
    sessionFetcher,
    setLoading,
  ]);

  return { results };
};
